import {
  Box,
  Button,
  ChevronDownBigIcon,
  ChevronUpBigIcon,
  constants,
  Skeleton,
  Text,
} from '@redbubble/design-system';
import Form from '@redbubble/design-system/dist/Form';
import FieldSet from '@redbubble/design-system/dist/Form/FieldSet';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import logger from '@redbubble/next/logger';
import Color from 'color';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingBoundary } from '../LoadingBoundary';
import { CONFIGURATOR_FORM_ID } from '../ProductConfigurator';

import { ColorFieldConfigurator } from '../ProductConfigurator/FieldConfigurators/ColorFieldConfigurator';
import { ColorOption } from '../ProductConfigurator/FieldConfigurators/types';
import { delegateField } from '../ProductConfigurator/fieldDelegationUtils';
import {
  AttributeName,
  type ConfiguratorField,
  type ConfiguratorValues,
  DSFormOnChangeCallback,
  SimpleAttribute,
} from '../ProductConfigurator/types';
import { useConfiguratorViewportProfile } from '../ProductConfigurator/useConfiguratorViewportProfile';
import { usePrefersReducedMotion } from '../ProductConfigurator/usePrefersReducedMotion';
import {
  configuratorValuesToFormikValues,
  getFallbackDefaultText,
} from '../ProductConfigurator/utils';
import styles from './CollapsibleColorPicker.module.scss';

type SwatchesResult = {
  swatches: ColorOption[];
  numberOfOtherAvailableColors: number;
};

export const messages = defineMessages({
  colors: {
    defaultMessage: `{availableColors, plural,
        =0 {see color}
        one {+{availableColors} color}
        other {+{availableColors} colors}}
      `,
  },
  colorsButtonAriaLabel: {
    defaultMessage: `See {availableColors, plural,
      one {{availableColors} available color}
      other {{availableColors} available colors}}
    `,
  },
});

export interface CollapsibleColorPickerProps {
  currentValues: ConfiguratorValues;
  onConfiguratorFormChange: DSFormOnChangeCallback;
  configuratorFields: ConfiguratorField[];
}

const getHexString = (hexColor: string): string =>
  hexColor.startsWith('#') ? hexColor : `#${hexColor}`;

const isLight = (hexColor: string) => {
  return Color(getHexString(hexColor)).luminosity() > 0.8;
};

const MAX_SWATCHES = 4;

const getSwatches = (
  currentColor: SimpleAttribute,
  colorsData: ColorOption[]
): SwatchesResult => {
  const currentColorData = colorsData.find(
    (color) => color.value === currentColor.value
  ) as ColorOption;

  const otherColorsData = colorsData.filter(
    (color) => color.value !== currentColor.value
  );

  const swatches =
    colorsData.length > MAX_SWATCHES
      ? [currentColorData, ...otherColorsData.slice(0, MAX_SWATCHES - 1)]
      : [currentColorData];

  const numberOfOtherAvailableColors = colorsData.length - swatches.length;

  return { swatches, numberOfOtherAvailableColors };
};

export const CollapsibleColorPicker: React.FC<CollapsibleColorPickerProps> = ({
  currentValues,
  onConfiguratorFormChange,
  configuratorFields,
}) => {
  const intl = useIntl();
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const [collapsibleOpen, setCollapsibleOpen] = useState(false);

  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (collapsibleRef.current && collapsibleOpen) {
      collapsibleRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }
  }, [collapsibleOpen, prefersReducedMotion]);

  const handleCollapsibleClick = () => {
    setCollapsibleOpen(!collapsibleOpen);
  };

  const viewportProfile = useConfiguratorViewportProfile();

  const colorConfiguratorField = configuratorFields.find(
    (field) =>
      field.name === AttributeName.BodyColor ||
      field.name === AttributeName.Color
  ) as ConfiguratorField;

  const colorAttributeName = colorConfiguratorField.name;
  const colorFieldData = delegateField(
    colorConfiguratorField.options,
    (option) =>
      logger.warn(
        {
          option,
          colorConfiguratorField,
          context: 'CollapsibleColorPicker',
          message: 'inventory item attribute has missing default text',
        },
        'inventory item attribute has missing default text'
      )
  );

  const currentColorValue =
    currentValues.find((value) => value.name === AttributeName.BodyColor) ||
    currentValues.find((value) => value.name === AttributeName.Color) ||
    colorConfiguratorField.options[0];

  const initialFormValues = useMemo(
    () => configuratorValuesToFormikValues([currentColorValue]),
    [currentColorValue]
  );

  if (!colorFieldData.success) {
    const err = { message: 'a configurator error has occurred' };
    logger.error(
      {
        err,
        colorFieldData,
        colorConfiguratorField,
        context: 'CollapsibleColorPicker',
        message: err.message,
      },
      err.message
    );
    return null;
  }

  const colorDefaultText = getFallbackDefaultText({
    attributeName: colorAttributeName,
    fallback: colorAttributeName,
    defaultText: colorConfiguratorField.defaultText,
  });

  const colorsData = colorFieldData.value.options as ColorOption[];

  const { swatches, numberOfOtherAvailableColors } = getSwatches(
    currentColorValue,
    colorsData
  );

  const availableColorsMessage = intl.formatMessage(messages.colors, {
    availableColors: numberOfOtherAvailableColors,
  });

  const colorsButtonAriaLabel = intl.formatMessage(
    messages.colorsButtonAriaLabel,
    {
      availableColors: colorsData.length,
    }
  );

  const getCollapsibleClasses = () => {
    const baseClasses = [styles.collapsibleColorsSection];
    if (collapsibleOpen) {
      baseClasses.push(styles.open);
    }
    if (!prefersReducedMotion) {
      baseClasses.push(styles.animated);
    }
    return baseClasses.join(' ');
  };

  return (
    <LoadingBoundary
      loadingComponent={<Skeleton height={32} width={191} animated />}
    >
      <Button
        type="button"
        intent={constants.PRIMARY}
        size={constants.SMALL}
        onClick={handleCollapsibleClick}
        iconAfter={
          collapsibleOpen ? <ChevronUpBigIcon /> : <ChevronDownBigIcon />
        }
        aria-expanded={!collapsibleOpen}
        aria-label={colorsButtonAriaLabel}
        data-testid="collapsible-color-picker"
      >
        <Box display="flex" paddingRight="xs" ref={collapsibleRef}>
          {swatches.map(({ hexColor }) => (
            <Box
              key={hexColor}
              className={styles.colorSwatch}
              style={{
                backgroundColor: getHexString(hexColor),
                border: isLight(getHexString(hexColor))
                  ? `2px solid ${constants.COLOR_NEUTRAL_200}`
                  : 'none',
              }}
              data-testid="color-swatch"
            />
          ))}
        </Box>
        <Text type="display6">{availableColorsMessage}</Text>
      </Button>

      <Box className={getCollapsibleClasses()}>
        <Form
          enableReinitialize
          initialValues={initialFormValues}
          onChange={onConfiguratorFormChange}
          profile={viewportProfile}
        >
          {() => {
            return (
              <FieldSet
                key={colorAttributeName}
                data-test-attribute-name={colorAttributeName}
                data-testid="product-configurator-item"
                id={`${CONFIGURATOR_FORM_ID}_${colorAttributeName}`}
              >
                <ColorFieldConfigurator
                  attributeName={colorAttributeName}
                  label={colorDefaultText}
                  options={colorsData}
                  currentValue={initialFormValues[colorAttributeName]}
                />
              </FieldSet>
            );
          }}
        </Form>
      </Box>
    </LoadingBoundary>
  );
};
